import { useCallback, useEffect, useMemo, useState } from 'react'
import * as api from '../lib/api'
import Select from '../components/Select'
import DateRangePicker from '../components/DateRangePicker'
import ActivityDrawer from '../components/ActivityDrawer'
import ClientLedgers from '../components/ClientLedgers'
import WorkPill from '../components/WorkPill'
import SampleTag from '../components/SampleTag'
import { SkeletonBlock } from '../components/Skeleton'
import { STAGES } from '../components/ReconciliationTracker'
import { CONVERSATION } from '../lib/chase'
import { whenText, dateText } from '../lib/when'
import { askRange, monthRange } from '../lib/period'
import { amountText, categoryLabel, durationText } from '../lib/remediation'
import { saveBlob } from '../lib/zip'
import {
  RANGE_PRESETS,
  presetRange,
  ledgerInRange,
  itemInRange,
  overview,
  byClient,
  byAccountant,
  workedNow,
  daysToReceive,
  toCsv,
} from '../lib/reports'

/**
 * Reports — everything Lereco tracks, for admins, in one place: the client
 * chase (WhatsApp/email to clients), their statements, reconciliation, the
 * post-reco follow-up, and our internal fixes. Four views of the same rows:
 * an overview, then by client, by ledger cycle and by accountant — each
 * table sortable, searchable and exportable as CSV, each row opening the
 * thing it's about.
 */

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'clients', label: 'Clients' },
  { value: 'ledgers', label: 'Ledgers' },
  { value: 'accountants', label: 'Accountants' },
]

const days = (n) => (n === null || n === undefined || !Number.isFinite(n) ? '—' : n < 1 ? '<1d' : `${n < 10 ? n.toFixed(1) : Math.round(n)}d`)
const pctText = (n) => (n === null || n === undefined ? '—' : `${n}%`)
const convLabel = (s) => (s ? CONVERSATION[s]?.label ?? s : '—')
const stageLabel = (s) => STAGES[s]?.label ?? s ?? '—'

function StagePill({ stage }) {
  const s = STAGES[stage] ?? STAGES.collecting
  return (
    <span className={`pill ${s.tone}`}>
      <span className="d" />
      {s.label}
    </span>
  )
}

function Seg({ options, value, onChange, label }) {
  return (
    <div className="seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={`seg-b${o.value === value ? ' on' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ── Overview pieces ─────────────────────────────────────────────────────────

function Kpi({ label, value, meta, tone }) {
  return (
    <div className="rp-kpi">
      <div className="rp-kpi-l">{label}</div>
      <div className={`rp-kpi-v${tone ? ` ${tone}` : ''}`}>{value}</div>
      {meta && <div className="rp-kpi-m">{meta}</div>}
    </div>
  )
}

function Section({ title, sub, children }) {
  return (
    <section className="panel rp-sec">
      <div className="panel-head">
        <div>
          <div className="panel-title">{title}</div>
          {sub && <div className="rp-sec-sub">{sub}</div>}
        </div>
      </div>
      <div className="rp-kpis">{children}</div>
    </section>
  )
}

/** Where every asked cycle has got to — one hue, share of "asked" on each bar. */
function Funnel({ steps }) {
  const top = steps[0]?.n || 0
  return (
    <section className="panel rp-sec">
      <div className="panel-head">
        <div>
          <div className="panel-title">Pipeline</div>
          <div className="rp-sec-sub">How far each ledger cycle has got, as a share of those we asked</div>
        </div>
      </div>
      <div className="rp-funnel">
        {steps.map((s) => {
          const share = top ? s.n / top : 0
          return (
            <div className="rp-fstep" key={s.key} title={`${s.label}: ${s.n}${top ? ` (${Math.round(share * 100)}% of asked)` : ''}`}>
              <span className="rp-flabel">{s.label}</span>
              <span className="rp-fbar">
                <span className="rp-ffill" style={{ width: `${Math.max(share * 100, s.n ? 2 : 0)}%` }} />
              </span>
              <span className="rp-fnum">
                {s.n}
                <span className="rp-fpct">{top ? `${Math.round(share * 100)}%` : ''}</span>
              </span>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function Overview({ data, ledgers, items }) {
  const o = useMemo(() => overview(ledgers, items), [ledgers, items])
  const c = o.chase
  const r = o.reco
  const p = o.postReco
  const n = o.internal
  return (
    <div className="rp-overview">
      <Section title="Client chase" sub="What we sent clients on WhatsApp and email, and what came back">
        <Kpi label="Ledger cycles" value={c.cycles} meta={`${c.clientsAsked} clients asked`} />
        <Kpi label="Messages sent" value={c.messages} meta={`got through · ${c.waOut + c.emailOut} attempted (${c.waOut} WA · ${c.emailOut} email)`} />
        <Kpi label="Delivered" value={pctText(c.deliveredPct)} meta={`read ${pctText(c.readPct)} (WhatsApp)`} />
        <Kpi label="Failed to send" value={c.failed} tone={c.failed ? 'bad' : ''} meta={c.sendFailing ? `${c.sendFailing} clients failing now` : 'none failing now'} />
        <Kpi label="Replied" value={c.replied} meta={`${pctText(c.replyRate)} of asked`} />
        <Kpi label="Statements in" value={c.received} tone="ok" meta={`${pctText(c.receivedRate)} of asked`} />
        <Kpi label="Time to statement" value={days(c.medianDaysToReceive)} meta={`median · ~${c.avgMessagesToReceive ? Math.round(c.avgMessagesToReceive) : '—'} messages each`} />
        <Kpi label="No reply yet" value={c.awaitingReply} />
        <Kpi label="Waiting on us" value={c.needsUs} tone={c.needsUs ? 'warn' : ''} meta="replied — needs reading or answering" />
        <Kpi label="Promised" value={c.promised} meta={c.promiseLate ? `${c.promiseLate} past their date` : 'none late'} />
        <Kpi label="Disputed / refused" value={c.disputed + c.refused} tone={c.disputed + c.refused ? 'bad' : ''} meta={`${c.disputed} disputed · ${c.refused} refused`} />
        <Kpi label="Bad contact / wrong file" value={c.badContact + c.badDocument} meta={`${c.badContact} contact · ${c.badDocument} file`} />
      </Section>

      <Section title="Reconciliation" sub="Once both ledgers are in — Claude compares them and says which side needs fixing">
        <Kpi label="Waiting on collection" value={r.waitingOnCollection} meta={`${r.needsConfirming} to confirm · ${r.ourSideMissing} our side missing`} />
        <Kpi label="Ready to reconcile" value={r.ready} tone={r.ready ? 'warn' : ''} />
        <Kpi label="Reconciling now" value={r.reconciling} />
        <Kpi label="Runs" value={r.runs} meta={`${r.runsFailed} failed`} />
        <Kpi label="Reconciled" value={r.reconciled} meta={`median ${days(r.medianDaysReceiveToReco)} after statement`} />
        <Kpi label="Clean" value={r.clean} tone="ok" meta={`${pctText(r.cleanRate)} of reconciled`} />
        <Kpi label="Client to fix" value={r.clientFix} />
        <Kpi label="We need to fix" value={r.ourFix} />
        <Kpi label="Both sides" value={r.bothFix} />
        <Kpi label="Run failed" value={r.failed} tone={r.failed ? 'bad' : ''} />
      </Section>

      <div className="rp-two">
        <Section title="Post-reconciliation" sub="Differences found — who we're waiting on to correct them">
          <Kpi label="Waiting on client" value={p.waitingOnClient} meta={p.oldestClientDays !== null ? `oldest ${days(p.oldestClientDays)}` : null} />
          <Kpi label="Needs a person" value={p.needsPerson} tone={p.needsPerson ? 'bad' : ''} meta="asked 7 times, no ledger — chase paused" />
          <Kpi label="Waiting on us" value={p.waitingOnUs} />
          <Kpi label="Clients with open fixes" value={p.clientsWithOpenFixes} />
        </Section>
        <Section title="Internal fixes" sub="Our-side corrections, worked by the accounts team on My Ledgers">
          <Kpi label="Raised" value={n.raised} meta={`${n.done} done · ${pctText(n.doneRate)}`} />
          <Kpi label="Open" value={n.open} tone={n.open ? 'warn' : ''} meta={`${n.inProgress} in progress · ${n.paused} paused · ${n.notStarted} not started`} />
          <Kpi label="Time spent" value={n.workedMs ? durationText(n.workedMs) : '—'} meta={`avg ${n.avgWorkMs ? durationText(n.avgWorkMs) : '—'} per fix`} />
          <Kpi label="Turnaround" value={days(n.medianTurnaroundDays)} meta="median, raised → done" />
          <Kpi label="Oldest open" value={days(n.oldestOpenDays)} tone={n.oldestOpenDays > 7 ? 'bad' : ''} />
          <Kpi label="Open value" value={n.openValue ? amountText(n.openValue) : '—'} meta={`${n.pauses} pauses in total`} />
        </Section>
      </div>

      <Funnel steps={o.funnel} />


      <div className="rp-foot">Updated {whenText(data.generatedAt)}</div>
    </div>
  )
}

// ── Tables ──────────────────────────────────────────────────────────────────

/**
 * A sortable table — `columns` = [{ key, label, render, sort, align, width, csv }].
 * Click a header to sort by it, again to flip.
 */
function SortTable({ rows, columns, initialSort, onRow, rowKey, empty, expanded, renderExpanded }) {
  const [sort, setSort] = useState(initialSort)
  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key)
    if (!col?.sort) return rows
    return [...rows].sort((a, b) => {
      const x = col.sort(a)
      const y = col.sort(b)
      const nx = x === null || x === undefined || x === ''
      const ny = y === null || y === undefined || y === ''
      if (nx && ny) return 0
      if (nx) return 1
      if (ny) return -1
      return (x < y ? -1 : x > y ? 1 : 0) * (sort.dir === 'asc' ? 1 : -1)
    })
  }, [rows, columns, sort])
  const grid = columns.map((c) => c.width ?? '1fr').join(' ')

  return (
    <div className="rp-tbl">
      <div className="thead" style={{ gridTemplateColumns: grid }}>
        {columns.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`rp-th${c.align === 'right' ? ' r' : ''}${sort.key === c.key ? ' on' : ''}`}
            onClick={() => c.sort && setSort((s) => ({ key: c.key, dir: s.key === c.key && s.dir === 'desc' ? 'asc' : 'desc' }))}
            disabled={!c.sort}
          >
            {c.label}
            {sort.key === c.key && <span className="rp-arrow">{sort.dir === 'desc' ? '↓' : '↑'}</span>}
          </button>
        ))}
      </div>
      {sorted.length === 0 && <div className="empty">{empty}</div>}
      {sorted.map((r) => (
        <div key={rowKey(r)}>
          <div
            className={`rowgrid${onRow ? ' rowbtn' : ''}${expanded === rowKey(r) ? ' on' : ''}`}
            style={{ gridTemplateColumns: grid }}
            role={onRow ? 'button' : undefined}
            tabIndex={onRow ? 0 : undefined}
            onClick={onRow ? () => onRow(r) : undefined}
            onKeyDown={
              onRow
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onRow(r)
                    }
                  }
                : undefined
            }
          >
            {columns.map((c) => (
              <span key={c.key} className={c.align === 'right' ? 'rp-r' : undefined}>
                {c.render(r)}
              </span>
            ))}
          </div>
          {expanded === rowKey(r) && renderExpanded?.(r)}
        </div>
      ))}
      <div className="rt-foot">
        {sorted.length} row{sorted.length === 1 ? '' : 's'}
      </div>
    </div>
  )
}

const two = (a, b) => (
  <>
    {a}
    {b !== undefined && b !== null && b !== '' && <span className="c-sub">{b}</span>}
  </>
)

const who = (r) =>
  two(
    <span className="c-name">{r.clientName || r.clientCode}</span>,
    <>
      {r.clientCode}
      <SampleTag show={Boolean(r.sample)} />
    </>
  )

const CLIENT_COLS = [
  { key: 'client', label: 'Client', width: 'minmax(160px,1.5fr)', render: who, sort: (r) => (r.clientName || r.clientCode).toLowerCase(), csv: (r) => `${r.clientName || ''} (${r.clientCode})` },
  { key: 'cycles', label: 'Cycles', width: '56px', align: 'right', render: (r) => r.cycles, sort: (r) => r.cycles, csv: (r) => r.cycles },
  { key: 'asked', label: 'First asked', width: '100px', render: (r) => (r.firstAskedAt ? dateText(r.firstAskedAt.slice(0, 10)) : '—'), sort: (r) => r.firstAskedAt, csv: (r) => r.firstAskedAt?.slice(0, 10) ?? '' },
  { key: 'messages', label: 'Messages', width: '90px', align: 'right', render: (r) => two(r.messages, `${r.waOut} WA · ${r.emailOut} email`), sort: (r) => r.messages, csv: (r) => r.messages },
  { key: 'replies', label: 'Replies', width: '84px', align: 'right', render: (r) => two(r.replies, r.lastReplyAt ? whenText(r.lastReplyAt) : 'never'), sort: (r) => r.replies, csv: (r) => r.replies },
  { key: 'received', label: 'Statements', width: '90px', align: 'right', render: (r) => two(`${r.received} / ${r.cycles}`, r.avgDaysToReceive !== null ? `avg ${days(r.avgDaysToReceive)}` : null), sort: (r) => r.received, csv: (r) => r.received },
  { key: 'conv', label: 'Latest chase', width: 'minmax(120px,1fr)', render: (r) => convLabel(r.conversationState), sort: (r) => convLabel(r.conversationState), csv: (r) => convLabel(r.conversationState) },
  { key: 'stage', label: 'Reconciliation', width: 'minmax(150px,1fr)', render: (r) => <StagePill stage={r.stage} />, sort: (r) => stageLabel(r.stage), csv: (r) => stageLabel(r.stage) },
  { key: 'fixes', label: 'Our fixes', width: '90px', align: 'right', render: (r) => two(r.fixesOpen ? `${r.fixesOpen} open` : '—', r.fixesDone ? `${r.fixesDone} done` : null), sort: (r) => r.fixesOpen * 1000 + r.fixesDone, csv: (r) => `${r.fixesOpen} open / ${r.fixesDone} done` },
]

const LEDGER_COLS = [
  { key: 'client', label: 'Client', width: 'minmax(150px,1.4fr)', render: who, sort: (r) => (r.clientName || r.clientCode).toLowerCase(), csv: (r) => `${r.clientName || ''} (${r.clientCode})` },
  { key: 'asked', label: 'Asked for', width: '130px', render: (r) => two(askRange(r.periodFrom, (r.receivedAt ?? new Date().toISOString()).slice(0, 10)) ?? '—', r.firstAskedAt ? `since ${dateText(r.firstAskedAt.slice(0, 10))}` : 'not asked yet'), sort: (r) => r.firstAskedAt, csv: (r) => r.firstAskedAt?.slice(0, 10) ?? '' },
  { key: 'messages', label: 'Sent', width: '70px', align: 'right', render: (r) => two(r.waOut + r.emailOut + r.callOut - r.failed, `${r.waOut} WA · ${r.emailOut} em`), sort: (r) => r.waOut + r.emailOut + r.callOut - r.failed, csv: (r) => r.waOut + r.emailOut + r.callOut - r.failed },
  { key: 'delivery', label: 'Delivered / read', width: '100px', align: 'right', render: (r) => two(`${r.delivered} / ${r.read}`, r.failed ? `${r.failed} failed` : null), sort: (r) => r.delivered, csv: (r) => `${r.delivered} delivered, ${r.read} read, ${r.failed} failed` },
  { key: 'replies', label: 'Replies', width: '70px', align: 'right', render: (r) => two(r.inbound, r.lastReplyAt ? whenText(r.lastReplyAt) : null), sort: (r) => r.inbound, csv: (r) => r.inbound },
  { key: 'conv', label: 'Chase', width: 'minmax(120px,1fr)', render: (r) => two(convLabel(r.conversationState), r.promisedFor ? `promised ${dateText(r.promisedFor)}` : r.sendFailing ? 'sending is failing' : null), sort: (r) => convLabel(r.conversationState), csv: (r) => convLabel(r.conversationState) },
  { key: 'received', label: 'Statement', width: '120px', render: (r) => (r.receivedAt ? two(dateText(r.receivedAt.slice(0, 10)), r.coversFrom && r.coversTo ? `covers ${monthRange(r.coversFrom, r.coversTo)}` : `${days(daysToReceive(r))} to arrive`) : <span className="mute">not yet</span>), sort: (r) => r.receivedAt, csv: (r) => r.receivedAt?.slice(0, 10) ?? '' },
  { key: 'stage', label: 'Reconciliation', width: 'minmax(150px,1fr)', render: (r) => two(<StagePill stage={r.stage} />, r.runs ? `${r.runs} run${r.runs === 1 ? '' : 's'}${r.lastRunAt ? ` · last ${whenText(r.lastRunAt)}` : ''}` : null), sort: (r) => stageLabel(r.stage), csv: (r) => stageLabel(r.stage) },
  { key: 'fixes', label: 'Our fixes', width: '76px', align: 'right', render: (r) => two(r.fixesOpen || '—', r.fixesDone ? `${r.fixesDone} done` : null), sort: (r) => r.fixesOpen, csv: (r) => `${r.fixesOpen} open / ${r.fixesDone} done` },
]

const ACC_COLS = [
  { key: 'name', label: 'Accountant', width: 'minmax(140px,1.3fr)', render: (r) => two(<span className="c-name">{r.name}</span>, r.category ? categoryLabel(r.category) : null), sort: (r) => r.name.toLowerCase(), csv: (r) => r.name },
  { key: 'open', label: 'Open', width: '130px', render: (r) => two(r.open, r.open ? `${r.inProgress} working · ${r.paused} paused · ${r.notStarted} new` : null), sort: (r) => r.open, csv: (r) => r.open },
  { key: 'done', label: 'Done', width: '56px', align: 'right', render: (r) => r.done, sort: (r) => r.done, csv: (r) => r.done },
  { key: 'worked', label: 'Time spent', width: '90px', align: 'right', render: (r) => two(r.workedMs ? durationText(r.workedMs) : '—', r.avgWorkMs ? `avg ${durationText(r.avgWorkMs)}` : null), sort: (r) => r.workedMs, csv: (r) => Math.round(r.workedMs / 60000) },
  { key: 'pickup', label: 'Picks up in', width: '84px', align: 'right', render: (r) => days(r.avgPickupDays), sort: (r) => r.avgPickupDays, csv: (r) => r.avgPickupDays?.toFixed(1) ?? '' },
  { key: 'turn', label: 'Turnaround', width: '84px', align: 'right', render: (r) => days(r.avgTurnaroundDays), sort: (r) => r.avgTurnaroundDays, csv: (r) => r.avgTurnaroundDays?.toFixed(1) ?? '' },
  { key: 'oldest', label: 'Oldest open', width: '84px', align: 'right', render: (r) => <span className={r.oldestOpenDays > 7 ? 'rp-bad' : undefined}>{days(r.oldestOpenDays)}</span>, sort: (r) => r.oldestOpenDays, csv: (r) => r.oldestOpenDays?.toFixed(1) ?? '' },
  { key: 'value', label: 'Open value', width: '100px', align: 'right', render: (r) => (r.openValue ? amountText(r.openValue) : '—'), sort: (r) => r.openValue, csv: (r) => r.openValue },
  { key: 'pauses', label: 'Pauses', width: '60px', align: 'right', render: (r) => r.pauses || '—', sort: (r) => r.pauses, csv: (r) => r.pauses },
  { key: 'nudges', label: 'Reminders', width: '86px', align: 'right', render: (r) => two(r.nudgeCount || '—', r.lastNudgeAt ? whenText(r.lastNudgeAt) : null), sort: (r) => r.nudgeCount, csv: (r) => r.nudgeCount },
  { key: 'last', label: 'Last activity', width: '96px', render: (r) => (r.lastActivityAt ? whenText(r.lastActivityAt) : '—'), sort: (r) => r.lastActivityAt, csv: (r) => r.lastActivityAt?.slice(0, 10) ?? '' },
]

/** An accountant row, opened: every fix on them, with where it stands. */
function AccountantFixes({ row }) {
  const list = [...row.items].sort((a, b) => (a.status === 'confirmed') - (b.status === 'confirmed') || new Date(a.createdAt) - new Date(b.createdAt))
  if (!list.length) return <div className="rp-expand empty">Nothing assigned in this range.</div>
  return (
    <div className="rp-expand">
      {list.map((i) => (
        <div className="rp-fix" key={i._id}>
          <span className="rm-code">
            {i.clientCode}
            <SampleTag show={Boolean(i.sample)} />
          </span>
          <span className="rm-cat">{categoryLabel(i.category)}</span>
          <WorkPill status={i.status} />
          <span className="c-sub" style={{ marginTop: 0 }}>raised {whenText(i.createdAt)}</span>
          <span className="c-sub" style={{ marginTop: 0 }}>{workedNow(i) ? `${durationText(workedNow(i))} on it` : 'no time logged'}</span>
          {i.status === 'paused' && i.pauseNote && <span className="rm-paused">{i.pauseNote}</span>}
          {i.confirmedAt && <span className="c-sub" style={{ marginTop: 0 }}>done {whenText(i.confirmedAt)}</span>}
          <span className="rp-fix-amt">{amountText(i.amount) ?? ''}</span>
        </div>
      ))}
    </div>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────

const STAGE_FILTERS = [
  { value: 'all', label: 'Any stage' },
  ...Object.entries(STAGES).map(([value, s]) => ({ value, label: s.label })),
]

export default function Reports() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('overview')
  const [preset, setPreset] = useState('all')
  const [custom, setCustom] = useState({ from: '', to: '' })
  const [text, setText] = useState('')
  const [stage, setStage] = useState('all')
  const [openClient, setOpenClient] = useState(null)
  const [openThread, setOpenThread] = useState(null)
  const [openAcc, setOpenAcc] = useState(null)

  const load = useCallback(async () => {
    try {
      setData(await api.getReports())
      setError(null)
    } catch (e) {
      setError(e.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const range = useMemo(() => presetRange(preset, custom), [preset, custom])

  const scoped = useMemo(() => {
    if (!data) return null
    return {
      ledgers: data.ledgers.filter((l) => ledgerInRange(l, range)),
      items: data.items.filter((i) => itemInRange(i, range)),
    }
  }, [data, range])

  const needle = text.trim().toLowerCase()
  const match = (r) => !needle || `${r.clientName ?? ''} ${r.clientCode ?? ''} ${r.name ?? ''} ${r.email ?? ''}`.toLowerCase().includes(needle)

  const clientRows = useMemo(() => (scoped ? byClient(scoped.ledgers, scoped.items).filter(match).filter((r) => stage === 'all' || r.stage === stage) : []), [scoped, needle, stage]) // eslint-disable-line react-hooks/exhaustive-deps
  const ledgerRows = useMemo(() => (scoped ? scoped.ledgers.filter(match).filter((r) => stage === 'all' || r.stage === stage) : []), [scoped, needle, stage]) // eslint-disable-line react-hooks/exhaustive-deps
  const accRows = useMemo(() => (scoped ? byAccountant(scoped.items, data.accountants).filter(match) : []), [scoped, data, needle]) // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    const [rows, cols, name] =
      tab === 'clients'
        ? [clientRows, CLIENT_COLS, 'clients']
        : tab === 'ledgers'
          ? [ledgerRows, LEDGER_COLS, 'ledgers']
          : [accRows, ACC_COLS, 'accountants']
    const suffix = range.from || range.to ? `-${range.from || 'start'}-to-${range.to || 'today'}` : ''
    saveBlob(new Blob([toCsv(rows, cols)], { type: 'text/csv' }), `lereco-${name}${suffix}.csv`)
  }

  const rangeText = range.from || range.to ? `${range.from ? dateText(range.from) : 'the start'} – ${range.to ? dateText(range.to) : 'today'}` : 'all time'

  return (
    <section className="screen">
      <div className="topbar">
        <div>
          <h1 className="page-title">Ledger Reports</h1>
          <div className="page-sub">
            Everything Lereco tracks — the client chase, statements, reconciliation, post-reco follow-up and our internal
            fixes — by client, by ledger and by accountant. Showing {rangeText}.
          </div>
        </div>
        <div className="rp-top-acts">
          <Select options={RANGE_PRESETS} value={preset} onChange={setPreset} title="Date range" />
          {preset === 'custom' && <DateRangePicker from={custom.from} to={custom.to} onChange={setCustom} />}
          {tab !== 'overview' && (
            <button type="button" className="btn btn-sm" onClick={exportCsv} disabled={!data}>
              Export CSV
            </button>
          )}
        </div>
      </div>

      <div className="rp-bar">
        <Seg options={TABS} value={tab} onChange={setTab} label="View" />
        {tab !== 'overview' && (
          <div className="rp-filters">
            <div className="search-box rt-search">
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={tab === 'accountants' ? 'Search accountant' : 'Search client name or code'}
                aria-label="Search"
              />
            </div>
            {tab !== 'accountants' && <Select options={STAGE_FILTERS} value={stage} onChange={setStage} title="Reconciliation stage" />}
          </div>
        )}
      </div>

      {error && <div className="note" style={{ marginBottom: 12 }}>{error}</div>}

      {!scoped && !error && (
        <div className="rp-overview">
          <SkeletonBlock height={180} style={{ borderRadius: 8 }} />
          <SkeletonBlock height={160} style={{ borderRadius: 8 }} />
        </div>
      )}

      {scoped && tab === 'overview' && <Overview data={data} ledgers={scoped.ledgers} items={scoped.items} />}

      {scoped && tab === 'clients' && (
        <div className="panel">
          <SortTable
            rows={clientRows}
            columns={CLIENT_COLS}
            initialSort={{ key: 'messages', dir: 'desc' }}
            rowKey={(r) => r.clientCode}
            onRow={(r) => setOpenClient({ clientCode: r.clientCode, clientName: r.clientName })}
            empty="No clients in this range."
          />
        </div>
      )}

      {scoped && tab === 'ledgers' && (
        <div className="panel">
          <SortTable
            rows={ledgerRows}
            columns={LEDGER_COLS}
            initialSort={{ key: 'asked', dir: 'desc' }}
            rowKey={(r) => r.ledgerRequestId}
            onRow={(r) =>
              setOpenThread({
                _id: r.ledgerRequestId,
                clientCode: r.clientCode,
                clientName: r.clientName,
                periodLabel: r.periodLabel,
                requestedAt: r.firstAskedAt,
                conversation: { state: r.conversationState, promisedFor: r.promisedFor },
              })
            }
            empty="No ledger cycles in this range."
          />
        </div>
      )}

      {scoped && tab === 'accountants' && (
        <div className="panel">
          <SortTable
            rows={accRows}
            columns={ACC_COLS}
            initialSort={{ key: 'open', dir: 'desc' }}
            rowKey={(r) => r.email}
            onRow={(r) => setOpenAcc((cur) => (cur === r.email ? null : r.email))}
            expanded={openAcc}
            renderExpanded={(r) => <AccountantFixes row={r} />}
            empty="No accountants."
          />
        </div>
      )}

      {openClient && <ClientLedgers request={openClient} onClose={() => setOpenClient(null)} />}
      {openThread && <ActivityDrawer request={openThread} onClose={() => setOpenThread(null)} />}
    </section>
  )
}
